"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MapPin, Calendar, ArrowRight, MessageSquare, CircleCheck as CheckCircle2, UserCheck, Sparkles } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqs = [{
  id: "item-1",
  question: "Why does my Lincoln business need local SEO rather than a generic national campaign?",
  answer: "Lincoln has a unique local market with distinct consumer habits, highly localized search queries, and specific regional competitors. A general national SEO campaign dilutes your effort across the whole UK. With targeted Lincoln local SEO, I optimise your digital presence specifically for high-intent customers searching in Lincoln, North Hykeham, Waddington, and surrounding Lincolnshire areas, driving direct phone calls, store visits, and local enquiries."
}, {
  id: "item-2",
  question: "Will I work directly with you, Yassine, or will my account be passed to an agency team?",
  answer: "You work directly with me at all times. I am an independent freelance SEO consultant, not a bloated agency with junior account managers. When you hire YE Consultancy, I personally audit your website, build your local strategy, execute technical fixes, and conduct monthly review calls. You get experienced, hands-on expertise with total transparency."
}, {
  id: "item-3",
  question: "How quickly can my Lincoln business appear in Google's Local 3-Pack?",
  answer: "Google Map Pack visibility depends on your current setup, local competition in Lincoln, and your Google Business Profile authority. Typically, initial improvements in local rankings and map visibility begin within 4 to 8 weeks. Comprehensive 3-Pack dominance for competitive Lincoln keywords usually builds solidly over 3 to 6 months of systematic optimization."
}, {
  id: "item-4",
  question: "Do you optimise for nearby Lincolnshire towns like Newark, Gainsborough, and Sleaford?",
  answer: "Yes, absolutely. Local SEO is not restricted to a single postcode. I create multi-location strategies and localized landing pages designed to capture search intent across your target service perimeter, whether that covers central Lincoln, Newark-on-Trent, Gainsborough, Sleaford, or the wider East Midlands region."
}, {
  id: "item-5",
  question: "What is included in your Google Business Profile (GBP) management for Lincoln?",
  answer: "Your Google Business Profile is often your most important lead generator. I handle complete profile setup, primary and secondary category selection, hyper-local service area configuration, geo-targeted photo updates, Q&A optimization, weekly post publishing, and review acquisition strategies tailored specifically for Lincoln customers."
}, {
  id: "item-6",
  question: "How do you earn authoritative local backlinks for Lincolnshire businesses?",
  answer: "I focus on genuine, high-quality local link building rather than cheap directory spam. This includes securing citations from verified Lincolnshire business registries, securing coverage in regional news outlets, partnering with local Lincolnshire organisations, and earning contextual links through industry-relevant local content."
}, {
  id: "item-7",
  question: "Can local SEO help my Lincoln business outrank larger national brands?",
  answer: "Yes. Google prioritises local relevance and geographic proximity for location-sensitive searches (e.g. 'solicitors in Lincoln' or 'commercial electrician Lincoln'). National brands often lack localized content, local citations, and an optimized Google Business Profile. By leveraging deep local signals, I help independent Lincoln businesses systematically beat national competitors in local map packs and localized organic results."
}, {
  id: "item-8",
  question: "What is the difference between local map pack rankings and local organic search results?",
  answer: "The Local Map Pack (the map block displaying 3 businesses at the top of Google) is driven heavily by your Google Business Profile, local citations, proximity, and review sentiment. Local organic results sit directly below the map and rely on website architecture, localized on-page content, technical SEO, and link authority. I optimise both channels simultaneously so your Lincoln business dominates both real estate areas."
}, {
  id: "item-9",
  question: "How do customer reviews impact my local Lincoln rankings, and do you help manage them?",
  answer: "Review velocity, overall star rating, and keywords used in customer reviews are top ranking factors for Google's local algorithm. I implement ethical review collection systems that make it easy for your satisfied Lincoln clients to leave detailed feedback containing relevant local keywords."
}, {
  id: "item-10",
  question: "How do you measure and report local SEO success and return on investment?",
  answer: "I provide clear, fluff-free monthly reports focusing on metrics that impact your revenue: local phone call volume, Google Maps direction requests, contact form conversions, and keyword ranking movement across Lincoln target terms. No vanity metrics or confusing jargon—just honest measurement of real growth."
}, {
  id: "item-11",
  question: "How does local SEO integrate with AI Search Optimisation (GEO) for Lincoln searches?",
  answer: "As Google Search Generative Experience (SGE), ChatGPT, and Perplexity evolve, local search queries increasingly extract structured data, entity relationships, and customer review sentiment. I optimize your Lincoln business entity across the web so AI search engines accurately recommend your services when users ask conversational AI questions about Lincoln providers."
}, {
  id: "item-12",
  question: "How do we get started with a tailored local SEO campaign for my Lincoln business?",
  answer: "It begins with a free, no-obligation SEO consultation directly with me. We discuss your business goals, target services in Lincoln, and current digital visibility. I then perform an initial evaluation and provide a custom action plan. You can book a time directly on my calendar."
}];
export default function Lincolnfaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="lincolnlocalseofaq" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {/* Decorative subtle background elements */}
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-secondary/50 rounded-full blur-3xl pointer-events-none transform translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none transform -translate-x-1/3 translate-y-1/3" />

      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        {/* Section Heading Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="text-center space-y-4 mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 justify-center">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-medium tracking-wide uppercase rounded-full">
              <MapPin className="size-3.5 text-primary mr-1" />
              Lincoln Local SEO Expertise
            </Badge>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-foreground">
            Frequently Asked Questions About <span className="text-primary">Lincoln Local SEO</span>
          </h2>

          <p className="text-muted-foreground text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            Clear, honest answers regarding local search optimization for Lincoln and Lincolnshire businesses. You work directly with me—Yassine Essaadi—to build lasting search authority.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6 pt-2 text-xs md:text-sm text-muted-foreground font-medium">
            <span className="flex items-center gap-1.5">
              <UserCheck className="size-4 text-primary" /> Direct Consultant Access
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" /> Transparent Reporting
            </span>
            <span className="flex items-center gap-1.5">
              <Sparkles className="size-4 text-primary" /> Tailored Strategy
            </span>
          </div>
        </motion.div>

        {/* Accordion List */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="bg-card rounded-2xl border border-border p-4 md:p-8 shadow-sm">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border border-border/80 rounded-xl px-4 md:px-6 bg-background transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-xs">
                <AccordionTrigger className="py-5 hover:no-underline text-left transition-colors group">
                  <span className="text-base md:text-lg font-semibold text-primary font-default group-hover:text-primary/80 transition-colors pr-2">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-5 pt-1 text-muted-foreground text-sm md:text-base leading-relaxed border-t border-border/40 mt-1">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* Direct Contact Callout Box */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="mt-12 md:mt-16 bg-muted rounded-2xl p-6 md:p-10 border border-border text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="flex items-center gap-2 text-primary font-semibold text-sm justify-center md:justify-start">
              <MessageSquare className="size-4" />
              <span>Have a specific question about your Lincoln business?</span>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground font-default">
              Let's talk directly about your SEO goals.
            </h3>
            <p className="text-muted-foreground text-sm md:text-base">
              No agency middlemen, no sales push. Book a free 30-minute consultation to review your current Lincoln search presence.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="rounded-lg shadow-sm font-medium">
              <Link to="/ContactPage">
                <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-lg font-medium">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}